import { ColorMatrixFilter, Container, Sprite, Text, Ticker, type Texture } from 'pixi.js';
import { friendOf, isWheelRun, RUN_NAME, type FriendInfo, type RoadLamp, type RoadState } from '@goodtime/shared';
import { explainFriendLamp, explainGold, explainLampGame, explainStars, starsLine, starsProgress } from '../../logic/road/explain';
import { lampFace, lampLabel, starRow } from './friendPieces';
import { formatShort } from '../../logic/road/format';
import { BoardPanel } from '../board/BoardPanel';
import { TitlePlaque } from '../board/TitlePlaque';
import { animate, COLOURS, displayStyle, uiStyle } from '../board/ui';
import { RoadStrip, type RoadArt } from './RoadStrip';

/** How much of the panel fits above the reels. */
export type RoadPanelMode = 'full' | 'compact' | 'chip';

/** Everything the panel draws with: the road's pieces plus pots and lock. */
export interface RoadPanelArt {
  (name: Parameters<RoadArt>[0] | 'potSilver' | 'potGold' | 'lock' | 'floor' | `road:${string}` | `shared:${string}`): Texture | null;
}

const LAMP_NAME: Record<string, string> = {
  coins_small: 'Coins',
  coins_big: 'Big coins',
  free_spins: 'Free spins',
  silver: 'Silver pot',
};

/** What each piece of the panel is for, in plain words (producer
 *  2026-10-14: "Players will want to know"). */
export const PANEL_EXPLAINERS = {
  silver: `Silver pot. It grows a little with every spin. When a ${RUN_NAME} lands on the silver pot, you win all of it.`,
  gold: (road: RoadState | null, themeId: string | null = null) => explainGold(road, themeId),
  lamp: (lamp: RoadLamp) => `${LAMP_NAME[lamp.id] ?? 'Prize'}: a ${RUN_NAME} that lands here pays ${lamp.label ?? 'this prize'}.`,
  darkLamp: 'A locked lamp. A friend lights it with their game when they join you.',
  /** `collector` is the slot's road symbol, in the singular ("flower"). */
  road: (collector: string) =>
    `Every ${collector} you land moves you along the road. Reach a prize to win it; reach the end for a ${RUN_NAME}.`,
  stars: (road: RoadState) => explainStars(road),
};

/**
 * The Royal Road panel above the reels, in Pixi: the lamps a Wheel Run can
 * land on, the silver and gold pots, and the road to the next run, on a
 * board (the slot's reel floor with a brass edge) titled on a plaque.
 * `full` shows everything; `compact` puts lamps and pots in one row over a
 * smaller road; `chip` is one line that opens the full panel on a tap.
 * Tapping a pot or a lamp says what it does. On a Wheel Run slot (no road of
 * its own) the slot's stars light the lamps: a dark lamp shows the stars it
 * needs, and the full panel has a row of the slot's stars and the coins to
 * the next lamp. While the gift square's gift waits, the token breathes and a
 * tap on the road opens the gift.
 */
export class RoadPanel extends Container {
  private readonly board = new BoardPanel();
  private readonly plaque = new TitlePlaque(RUN_NAME.toUpperCase());
  private readonly body = new Container();
  private readonly strip: RoadStrip;
  private readonly bubble = new Container();
  private readonly unlit = new ColorMatrixFilter();
  private road: RoadState | null = null;
  private mode: RoadPanelMode = 'full';
  private w = 360;
  private h = 180;
  private lastSpinKey = 0;
  private bubbleTimer: ReturnType<typeof setTimeout> | null = null;
  /** A tap on the chip (the host opens the full panel). */
  onChipTap: (() => void) | null = null;
  /** What the slot's road symbol is called ("flower" on Forest). */
  collectorName = 'flower';
  /** A tap on a lit friend's lamp whose first game is still free. */
  onFriendPlay: ((friend: FriendInfo) => void) | null = null;
  /** A tap on a lit Wheel Run lamp whose first game is still free. */
  onLampPlay: ((lamp: RoadLamp) => void) | null = null;
  /** A tap on the road while the gift square's gift waits. */
  onGiftTap: (() => void) | null = null;
  /** The slot the panel is on: a Wheel Run slot's games are named after it. */
  themeId: string | null = null;
  private breath = 0;
  private breathing = false;
  private readonly breathe = (ticker: Ticker): void => {
    this.breath += ticker.deltaMS;
    this.strip.setTokenScale(1 + 0.08 * Math.sin((this.breath / 900) * Math.PI * 2));
  };

  constructor(private readonly art: RoadPanelArt) {
    super();
    this.unlit.desaturate();
    this.unlit.brightness(0.45, true);
    this.strip = new RoadStrip((name) => art(name));
    this.addChild(this.board, this.body, this.strip, this.plaque, this.bubble);
    this.eventMode = 'passive';
  }

  setLayout(mode: RoadPanelMode, x: number, y: number, width: number, height: number): void {
    this.mode = mode;
    this.position.set(Math.round(x), Math.round(y));
    this.w = Math.round(width);
    this.h = Math.round(height);
    this.draw();
  }

  /** A new road state; `spinKey` changes when a spin produced it. */
  setRoad(road: RoadState, spinKey: number): void {
    const rolled = spinKey !== this.lastSpinKey;
    this.lastSpinKey = spinKey;
    this.road = road;
    this.draw();
    const { trail } = road;
    const moved = trail.steps.length > 0 || trail.collected > 0;
    this.strip.update(
      { squares: trail.squares, position: trail.position, meter: trail.meter, cost: trail.cost },
      { rolled: rolled && moved, collected: trail.collected, hop: trail.steps.length > 0 },
    );
    this.setBreathing(giftWaiting(road));
  }

  /** The token breathes while the gift square's gift waits to be opened. */
  private setBreathing(on: boolean): void {
    if (on === this.breathing) return;
    this.breathing = on;
    if (on) Ticker.shared.add(this.breathe);
    else {
      Ticker.shared.remove(this.breathe);
      this.strip.setTokenScale(1);
    }
  }

  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    Ticker.shared.remove(this.breathe);
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer);
    super.destroy(options);
  }

  /** The road's art has loaded: draw it in. */
  refreshArt(): void {
    this.board.setFloor(this.art('floor'));
    this.draw();
    this.strip.redraw();
  }

  /** Centre of the silver pot, for coins flying out of it. */
  get centre(): { x: number; y: number } {
    return { x: this.x + this.w / 2, y: this.y + this.h / 2 };
  }

  /** Where a trail square's coins fly from: the road's token, or the chip
   *  when the road is folded away. */
  get prizePoint(): { x: number; y: number } {
    return this.mode === 'chip' ? this.centre : this.strip.tokenPoint;
  }

  private draw(): void {
    const road = this.road;
    this.board.setFloor(this.art('floor'));
    this.board.resize(this.w, this.h);
    this.body.removeChildren().forEach((c) => c.destroy({ children: true }));
    this.plaque.visible = this.mode === 'full';
    this.plaque.position.set(this.w / 2, 0);
    this.strip.visible = this.mode !== 'chip';
    if (!road) return;
    if (this.mode === 'chip') {
      this.drawChip(road);
      return;
    }
    // Opened from the chip: a tap on a lamp or pot explains it, and no
    // longer folds the panel away.
    this.body.removeAllListeners('pointertap');
    this.body.eventMode = 'passive';
    this.body.hitArea = null;
    this.body.cursor = 'default';
    const pad = this.mode === 'full' ? 12 : 8;
    const inner = this.w - pad * 2;
    const wide = this.mode === 'full' && inner >= 500;
    const compact = this.mode === 'compact';
    const potsW = wide ? 170 : 0;
    // A Wheel Run slot's stars: under the lamps when the board is wide; on a
    // phone beside smaller pots, in the pots' row.
    const stars = this.mode === 'full' && isWheelRun(road);
    const lampSize = compact ? 28 : Math.max(30, Math.min(wide ? 46 : 40, Math.floor((inner - potsW) / road.lamps.length) - 8));
    const top = this.mode === 'full' ? 16 : 6;

    // Lamps.
    const lamps = new Container();
    const lampsW = wide ? inner - potsW - 14 : compact ? inner - 110 : inner;
    const step = lampsW / road.lamps.length;
    road.lamps.forEach((lamp, i) => {
      const l = this.buildLamp(lamp, lampSize, step - 4);
      l.position.set(step * i + step / 2, lampSize / 2);
      lamps.addChild(l);
    });
    lamps.position.set(pad, top);
    this.body.addChild(lamps);

    // Pots.
    const named = !compact && !(stars && !wide);
    const silver = this.buildPot('silver', road.silverPot, true, named, wide ? potsW : 0);
    const gold = this.buildPot('gold', road.goldPot, road.goldOpen, named, wide ? potsW : 0);
    if (wide) {
      silver.position.set(this.w - pad - potsW, top + 4);
      gold.position.set(this.w - pad - potsW, top + 4 + 34);
    } else if (compact) {
      silver.position.set(this.w - pad - 100, top);
      gold.position.set(this.w - pad - 100, top + 24);
    } else if (stars) {
      // The stars on the left, the pots (unnamed) on the right.
      gold.position.set(this.w - pad - gold.width, top + lampSize + 24);
      silver.position.set(gold.x - 14 - silver.width, top + lampSize + 24);
    } else {
      const each = Math.min(170, (inner - 16) / 2);
      silver.position.set(this.w / 2 - each - 8, top + lampSize + 22);
      gold.position.set(this.w / 2 + 8, top + lampSize + 22);
    }
    this.body.addChild(silver, gold);
    if (stars) {
      const row = this.buildStars(road, wide ? 17 : 15, wide ? lampsW : silver.x - pad - 10, !wide);
      row.position.set(pad, wide ? top + lampSize + 18 : top + lampSize + 25);
      this.body.addChild(row);
    }

    // The road along the bottom.
    const size = compact ? { tokenSq: 34, sq: 28 } : wide ? { tokenSq: 50, sq: 42 } : { tokenSq: 44, sq: 36 };
    this.strip.layoutStrip(inner, size.tokenSq, size.sq, compact || !wide);
    const stripH = RoadStrip.heightFor(size.tokenSq, size.sq, compact || !wide);
    this.strip.position.set(pad, this.h - stripH - 2);
    this.strip.eventMode = 'static';
    this.strip.cursor = 'help';
    this.strip.removeAllListeners('pointertap');
    this.strip.on('pointertap', () => {
      // A gift waiting on the road opens; otherwise the road says how it works.
      if (this.road && giftWaiting(this.road) && this.onGiftTap) this.onGiftTap();
      else this.explain(PANEL_EXPLAINERS.road(this.collectorName), { x: this.w / 2, y: this.strip.y });
    });
  }

  /**
   * The slot's stars as pictures (lit, then dark), and the coins to the
   * next lamp: `size` a star, at most `width` across. A tap says how stars
   * come and what the next one lights.
   */
  private buildStars(road: RoadState, size: number, width: number, short: boolean): Container {
    const c = new Container();
    const { stars } = starsProgress(road);
    const lit = starRow(stars, size, (name) => this.art(name as `road:${string}`));
    const dark = starRow(3 - stars, size, (name) => this.art(name as `road:${string}`), false);
    const gap = Math.max(1, Math.round(size * 0.12));
    const litW = stars * size + Math.max(0, stars - 1) * gap;
    lit.x = litW / 2;
    dark.x = litW + (stars > 0 ? gap : 0) + ((3 - stars) * size + Math.max(0, 2 - stars) * gap) / 2;
    const rowW = 3 * size + 2 * gap;
    const line = new Text({ text: starsLine(road, short), style: uiStyle(short ? 12 : 13, COLOURS.gold) });
    line.anchor.set(0, 0.5);
    line.position.set(rowW + 8, size / 2);
    fitWidth(line, width - rowW - 8);
    c.addChild(lit, dark, line);
    c.label = 'road-stars';
    c.eventMode = 'static';
    c.cursor = 'help';
    c.on('pointertap', () => this.explain(PANEL_EXPLAINERS.stars(road), this.toLocal(c.getGlobalPosition())));
    return c;
  }

  private drawChip(road: RoadState): void {
    const title = new Text({ text: RUN_NAME.toUpperCase(), style: displayStyle(15) });
    title.anchor.set(0, 0.5);
    title.position.set(12, this.h / 2);
    const meter = this.buildMeter(road.trail.meter, road.trail.cost, 48);
    meter.position.set(title.x + title.width + 14, this.h / 2);
    const silver = this.buildPot('silver', road.silverPot, true, false, 0);
    silver.position.set(this.w - 96, this.h / 2 - 11);
    this.body.addChild(title, meter, silver);
    this.body.eventMode = 'static';
    this.body.cursor = 'pointer';
    this.body.hitArea = { contains: (x: number, y: number) => x >= 0 && y >= 0 && x <= this.w && y <= this.h };
    this.body.removeAllListeners('pointertap');
    this.body.on('pointertap', () => this.onChipTap?.());
  }

  private buildLamp(lamp: RoadLamp, size: number, labelWidth: number): Container {
    if (friendOf(lamp) || lamp.game) return this.buildFriendLamp(lamp, size, labelWidth);
    const c = new Container();
    const lens = this.art('lamp');
    if (lens) {
      const s = new Sprite(lens);
      s.anchor.set(0.5);
      s.width = s.height = size;
      if (!lamp.lit) s.filters = [this.unlit];
      c.addChild(s);
    }
    const iconName = !lamp.lit ? 'lock' : lamp.id === 'free_spins' ? 'spin' : lamp.id === 'silver' ? 'potSilver' : 'coin';
    const icon = this.art(iconName);
    if (icon) {
      const s = new Sprite(icon);
      s.anchor.set(0.5);
      s.width = s.height = Math.round(size * (lamp.lit ? 0.46 : 0.36));
      s.alpha = lamp.lit ? 1 : 0.75;
      c.addChild(s);
    }
    const label = new Text({
      text: lamp.lit ? (lamp.label ?? '') : '?',
      style: displayStyle(Math.max(11, Math.round(size * 0.26)), lamp.lit ? COLOURS.gold : 0xb8a888),
    });
    label.anchor.set(0.5, 0);
    label.y = size / 2 + 1;
    fitWidth(label, labelWidth);
    c.addChild(label);
    c.eventMode = 'static';
    c.cursor = 'help';
    c.on('pointertap', () =>
      this.explain(lamp.lit ? PANEL_EXPLAINERS.lamp(lamp) : PANEL_EXPLAINERS.darkLamp, this.toLocal(c.getGlobalPosition())),
    );
    return c;
  }

  /**
   * A friend's lamp: dark, it shows whose it is (their shadow under a lock,
   * their name); lit, their game and its name, with a FREE plate while their
   * first game is free. A Wheel Run lamp is the same with no friend: dark, a
   * lock and the stars it needs. A tap says what it means, or offers the
   * free game.
   */
  private buildFriendLamp(lamp: RoadLamp, size: number, labelWidth: number): Container {
    const c = new Container();
    const art = (name: string) => this.art(name as `road:${string}`);
    c.addChild(lampFace(lamp, size, art, false));
    const label = new Text({
      text: lampLabel(lamp),
      style: displayStyle(Math.max(11, Math.round(size * 0.26)), lamp.lit ? COLOURS.gold : 0xb8a888),
    });
    label.anchor.set(0.5, 0);
    label.y = size / 2 + 1;
    fitWidth(label, labelWidth);
    c.addChild(label);
    if (!lamp.lit && lamp.needs) {
      // The stars that light it, where its name would be.
      const needs = starRow(lamp.needs, Math.max(9, Math.round(size * 0.28)), art);
      needs.y = size / 2 + 2;
      needs.label = `lamp-needs-${lamp.needs}`;
      // No wider than the lamp's own room, or a narrow row's three stars run
      // into the next lamp's two (a compact panel on a small phone).
      if (labelWidth > 0 && needs.width > labelWidth) needs.scale.set(labelWidth / needs.width);
      c.addChild(needs);
    }
    const friend = friendOf(lamp);
    const free = lamp.lit && lamp.freePlay === true;
    if (free) {
      // Across the foot of the lamp, clear of the panel's edge and its
      // neighbours.
      const tag = new TitlePlaque('FREE', Math.max(9, Math.round(size * 0.22)), 5);
      tag.position.set(0, size / 2 - tag.plateHeight / 2);
      tag.label = 'lamp-free';
      c.addChild(tag);
    }
    c.label = `lamp-${lamp.id}`;
    c.eventMode = 'static';
    c.cursor = free ? 'pointer' : 'help';
    c.on('pointertap', () => {
      if (free && friend && this.onFriendPlay) this.onFriendPlay(friend);
      else if (free && !friend && this.onLampPlay) this.onLampPlay(lamp);
      else {
        const words = friend
          ? explainFriendLamp(lamp)
          : this.themeId ? explainLampGame(lamp, this.themeId) : null;
        this.explain(words ?? PANEL_EXPLAINERS.darkLamp, this.toLocal(c.getGlobalPosition()));
      }
    });
    return c;
  }

  /** A pot: its art, its name (when there's room) and what's in it. */
  private buildPot(kind: 'silver' | 'gold', value: number, open: boolean, named: boolean, width: number): Container {
    const c = new Container();
    const art = this.art(kind === 'silver' ? 'potSilver' : 'potGold');
    const artSize = named ? 28 : 20;
    if (art) {
      const s = new Sprite(art);
      s.width = s.height = artSize;
      s.y = -3;
      if (!open) s.filters = [this.unlit];
      c.addChild(s);
    }
    let x = artSize + 6;
    if (named) {
      const name = new Text({ text: kind === 'silver' ? 'SILVER POT' : 'GOLD POT', style: uiStyle(11, 0xf0dcb4) });
      name.position.set(x, 5);
      c.addChild(name);
      x += name.width + 8;
    }
    const amount = new Text({ text: formatShort(value), style: displayStyle(named ? 16 : 13, kind === 'silver' ? 0xeef2f8 : COLOURS.gold) });
    amount.position.set(width > 0 ? Math.max(x, width - amount.width) : x, named ? 1 : 0);
    c.addChild(amount);
    c.alpha = open ? 1 : 0.7;
    c.eventMode = 'static';
    c.cursor = 'help';
    c.on('pointertap', () => {
      const at = this.toLocal(c.getGlobalPosition());
      this.explain(kind === 'silver' ? PANEL_EXPLAINERS.silver : PANEL_EXPLAINERS.gold(this.road, this.themeId), { x: at.x + 40, y: at.y + 10 });
    });
    return c;
  }

  /** The chip's progress: a stretch of road that lights up, and the count. */
  private buildMeter(meter: number, cost: number, width: number): Container {
    const c = new Container();
    const collector = this.art('collector');
    if (collector) {
      const s = new Sprite(collector);
      s.anchor.set(0, 0.5);
      s.width = s.height = 22;
      c.addChild(s);
    }
    const cobbles = this.art('path') ?? this.art('cobbles');
    const pct = Math.max(0, Math.min(1, meter / cost));
    if (cobbles) {
      const dark = new Sprite(cobbles);
      dark.position.set(28, -5);
      dark.width = width;
      dark.height = 10;
      dark.tint = 0x5a5048;
      const lit = new Sprite(cobbles);
      lit.position.set(28, -5);
      lit.width = Math.max(1, width * pct);
      lit.height = 10;
      c.addChild(dark, lit);
    }
    const count = new Text({ text: `${meter}/${cost}`, style: uiStyle(13, COLOURS.gold) });
    count.anchor.set(0, 0.5);
    count.x = 28 + width + 6;
    c.addChild(count);
    return c;
  }

  /** Say something in the panel's bubble at a point in panel space. */
  explainAt(text: string, at: { x: number; y: number }): void {
    this.explain(text, at);
  }

  /** Say something over the road (a gift that couldn't open just now). */
  sayOnRoad(text: string): void {
    this.explain(text, { x: this.w / 2, y: this.strip.y });
  }

  /** Say what a piece does, in a bubble under it, for a few seconds. */
  private explain(text: string, at: { x: number; y: number }): void {
    if (this.bubbleTimer) clearTimeout(this.bubbleTimer);
    this.bubble.removeChildren().forEach((c) => c.destroy({ children: true }));
    const width = Math.min(280, this.w - 16);
    const label = new Text({ text, style: { ...uiStyle(14), wordWrap: true, wordWrapWidth: width - 24, lineHeight: 18 } });
    const board = new BoardPanel({ floor: this.art('floor'), radius: 10 });
    board.resize(width, label.height + 20);
    label.position.set(12, 10);
    this.bubble.addChild(board, label);
    const x = Math.max(8, Math.min(this.w - width - 8, at.x - width / 2));
    this.bubble.position.set(x, at.y + 18);
    this.bubble.alpha = 0;
    this.bubble.visible = true;
    void animate(160, (t) => (this.bubble.alpha = t));
    this.bubbleTimer = setTimeout(() => {
      void animate(200, (t) => (this.bubble.alpha = 1 - t)).then(() => (this.bubble.visible = false));
    }, 4200);
  }
}

/** A gift from the gift square waits to be opened (or its game is open). */
function giftWaiting(road: RoadState): boolean {
  return (!!road.gift && !road.gift.finished) || !!road.giftGame;
}

/** Shrink a label to `width` when it's wider (a long name in a narrow row). */
function fitWidth(label: Text, width: number): void {
  if (width > 0 && label.width > width) label.scale.set(width / label.width);
}
